import cx from 'classnames';
import React, { forwardRef, useMemo } from 'react';

export interface ProgressBarGradientProps
  extends React.HTMLAttributes<HTMLDivElement> {
  min: number;
  max: number;
  progress: number;
  className?: string;
}

/**
 * Gradient-styled progress bar that mirrors the UI library ProgressBar API.
 * Renders a Tailwind-styled gradient fill.
 */
const ProgressBarGradient = forwardRef<
  HTMLDivElement,
  ProgressBarGradientProps
>(({ min, max, progress, className, ...props }, ref) => {
  const percentage = useMemo(() => {
    const span = Math.max(0, max - min);
    if (span === 0) return 0;
    const pct = ((progress - min) / span) * 100;
    return Math.max(0, Math.min(100, pct));
  }, [min, max, progress]);

  // Clamp values for ARIA to ensure valid range
  const ariaNow = Math.min(Math.max(progress, min), max);

  return (
    <div
      ref={ref}
      role="progressbar"
      aria-valuemin={min}
      aria-valuemax={max}
      aria-valuenow={ariaNow}
      aria-valuetext={`${Math.round(percentage)}%`}
      className={cx(className)}
      {...props}
    >
      <div className="yst-relative yst-h-2 yst-w-full yst-overflow-hidden yst-rounded-full yst-bg-slate-200/40">
        <div
          className="yst-absolute yst-left-0 yst-top-0 yst-h-full yst-rounded-full yst-bg-[linear-gradient(97.38deg,_#A61E69_0%,_#6366F1_100%)] yst-transition-[width] yst-duration-300 yst-ease-out"
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
});

ProgressBarGradient.displayName = 'ProgressBarGradient';

export default ProgressBarGradient;
